import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Sparkles, Stars, Clock, ShieldCheck, ArrowRight, Check, Target, Users, Quote, Star, CalendarClock, Compass, Globe } from 'lucide-react';
import { fetchRegion, formatPrice } from '@/lib/region';

const HERO_BG = 'https://images.pexels.com/photos/6141905/pexels-photo-6141905.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const FEATURE_IMG = 'https://images.unsplash.com/photo-1676188114945-efdc6d290dbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvc21pYyUyMGRhcmslMjBibHVlJTIwZ29sZHxlbnwwfHx8fDE3NzcwNDMwODh8MA&ixlib=rb-4.1.0&q=85';

// Round 40i — region-aware pricing on the landing page. Resolved
// once on mount; everything below reads `currency` + `symbol` from
// here. India users see ₹; non-India users see $ and the PayPal
// branch. Falls back to INR on detection failure so India never
// regresses.
function useLandingRegion() {
    const [region, setRegion] = useState({ currency: 'INR', symbol: '₹', country: 'IN' });
    useEffect(() => {
        (async () => {
            // The /region/me endpoint requires auth — for the public
            // landing page we use IP detection only (cheap, optional).
            // If it fails, we keep the INR default.
            try {
                const r = await fetchRegion();
                setRegion(r);
            } catch { /* keep INR default */ }
        })();
    }, []);
    return region;
}

export default function Landing() {
    const region = useLandingRegion();
    return (
        <div className="relative z-10">
            <TopNav region={region} />
            <Hero region={region} />
            <Features />
            <BirthTimeRectification region={region} />
            <HowItWorks />
            <Testimonials />
            <Pricing region={region} />
            <Experts />
            <Faq />
            <Footer />
        </div>
    );
}

function TopNav({ region }) {
    const isIntl = region?.currency === 'USD';
    return (
        <header className="sticky top-0 z-50 bg-obsidian/70 backdrop-blur-xl border-b border-marigold/10">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="top-logo">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <circle cx="16" cy="16" r="13" stroke="#F59E0B" strokeWidth="1" opacity="0.6" />
                        <circle cx="16" cy="16" r="5" stroke="#FDE68A" strokeWidth="0.8" />
                    </svg>
                    <span className="font-serif text-2xl text-parchment">AstroConsult</span>
                </Link>
                <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
                    <a href="#how" className="hover:text-parchment transition-colors">How it works</a>
                    <a href="#pricing" className="hover:text-parchment transition-colors">Pricing</a>
                    <a href="#experts" className="hover:text-parchment transition-colors">Experts</a>
                    <a href="#faq" className="hover:text-parchment transition-colors">FAQ</a>
                </nav>
                <div className="flex items-center gap-3">
                    {isIntl && (
                        <span
                            className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-slate-400 border border-marigold/20 rounded-full px-2.5 py-1"
                            data-testid="nav-region-badge"
                            title="Auto-detected from your location"
                        >
                            <Globe size={11} className="text-marigold" />
                            {region.country} · {region.currency}
                        </span>
                    )}
                    <Link to="/login" data-testid="nav-login">
                        <Button variant="ghost" className="text-parchment hover:text-marigold hover:bg-transparent">
                            Sign in
                        </Button>
                    </Link>
                    <Link to="/signup" data-testid="nav-signup">
                        <Button className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none font-medium">
                            Get started
                        </Button>
                    </Link>
                </div>
            </div>
        </header>
    );
}

function Hero({ region }) {
    const isIntl = region?.currency === 'USD';
    const rate = isIntl ? '$1 / minute' : '₹25 / minute';
    return (
        <section className="relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-50"
                style={{
                    backgroundImage: `url(${HERO_BG})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/40 to-obsidian" aria-hidden="true" />

            <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-40 grid lg:grid-cols-12 gap-12 items-center stagger">
                <div className="lg:col-span-7">
                    <div className="overline mb-6" data-testid="hero-overline">
                        · Vedic · KP · Horary ·
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-parchment leading-[1.05] tracking-tight">
                        Ancient wisdom,<br />
                        <span className="text-marigold italic">precisely</span> calculated.
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-xl leading-relaxed">
                        <span className="block font-serif italic text-parchment text-2xl mb-4">The stars have always known.</span>
                        For thousands of years, Vedic astrologers mapped the sky to understand human destiny.
                        Today, AstroConsult brings that ancient wisdom into the present — with the precision of
                        modern technology and the warmth of a trusted guide.
                        <span className="block mt-4 text-marigold">Ask your question. The planets are listening.</span>
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link to="/signup" data-testid="hero-cta-primary">
                            <Button size="lg" className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none font-medium h-12 px-8">
                                Start chatting with Guruji <ArrowRight className="ml-2" size={18} />
                            </Button>
                        </Link>
                        <a href="#how" data-testid="hero-cta-secondary">
                            <Button variant="outline" size="lg" className="bg-transparent border-marigold/40 text-parchment hover:bg-marigold/10 hover:text-parchment rounded-none h-12 px-8">
                                How it works
                            </Button>
                        </a>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
                        <span data-testid="hero-rate">{rate} — pay only while you chat</span>
                        <span className="w-1 h-1 rounded-full bg-slate-600" />
                        <span>Pause anytime · wallet balance never expires</span>
                        {isIntl && (
                            <>
                                <span className="w-1 h-1 rounded-full bg-slate-600" />
                                <span className="text-marigold/80" data-testid="hero-global-tagline">
                                    Available worldwide · pay in your local currency
                                </span>
                            </>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-5 hidden lg:block">
                    <HeroOrnament />
                </div>
            </div>
        </section>
    );
}

function HeroOrnament() {
    return (
        <div className="relative aspect-square max-w-md mx-auto">
            <svg viewBox="0 0 400 400" className="w-full h-full animate-float-slow">
                <defs>
                    <radialGradient id="grad" cx="50%" cy="50%">
                        <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.2" />
                        <stop offset="70%" stopColor="#F59E0B" stopOpacity="0" />
                    </radialGradient>
                </defs>
                <circle cx="200" cy="200" r="180" fill="url(#grad)" />
                {/* Outer zodiac ring */}
                <circle cx="200" cy="200" r="170" fill="none" stroke="#B45309" strokeWidth="0.8" opacity="0.7" />
                <circle cx="200" cy="200" r="140" fill="none" stroke="#B45309" strokeWidth="0.5" opacity="0.5" />
                <circle cx="200" cy="200" r="90" fill="none" stroke="#F59E0B" strokeWidth="0.8" opacity="0.8" />
                <circle cx="200" cy="200" r="50" fill="none" stroke="#FDE68A" strokeWidth="0.5" />
                {/* 12 zodiac spokes */}
                {Array.from({ length: 12 }).map((_, i) => {
                    const a = (i * 30) * Math.PI / 180;
                    const x1 = 200 + 90 * Math.cos(a), y1 = 200 + 90 * Math.sin(a);
                    const x2 = 200 + 170 * Math.cos(a), y2 = 200 + 170 * Math.sin(a);
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#B45309" strokeWidth="0.6" opacity="0.6" />;
                })}
                {/* Sanskrit glyph-like marks */}
                {['सू', 'चं', 'मं', 'बु', 'गु', 'शु', 'श', 'रा', 'के'].map((g, i) => {
                    const a = (i * 40 - 90) * Math.PI / 180;
                    const x = 200 + 115 * Math.cos(a);
                    const y = 200 + 115 * Math.sin(a);
                    return (
                        <text key={i} x={x} y={y} fontSize="16" fill="#FDE68A" textAnchor="middle"
                            dominantBaseline="middle" fontFamily="Cormorant Garamond, serif">
                            {g}
                        </text>
                    );
                })}
                <text x="200" y="200" fontSize="44" fill="#F59E0B" textAnchor="middle"
                    dominantBaseline="middle" fontFamily="Cormorant Garamond, serif" fontStyle="italic">
                    ॐ
                </text>
            </svg>
        </div>
    );
}

function Trust() {
    // Intentionally left blank — stats removed pending updated metrics.
    return null;
}

function Features() {
    const features = [
        {
            icon: Stars,
            title: 'Proven across millions of charts',
            body: "Since the 1950s, KP has been tested against real births, real events, and real outcomes by astrologers worldwide. It doesn't guess. It calculates.",
        },
        {
            icon: Target,
            title: 'Your question. Your chart.',
            body: 'Every reading is cast for the exact moment you ask — your unique planetary positions, your sub-lords, your dasha period. No generic horoscopes. No one-size-fits-all answers.',
        },
        {
            icon: ShieldCheck,
            title: 'The maths decides. Engine computes.',
            body: 'Every prediction is produced by a deterministic algorithm built scientifically on the KP rule set — never a guess. Significators, house lords, sub-lords and your current dasha chain are combined into a verdict before any words are written. Back-testing on historical KP-book cases shows roughly 75% accuracy on questions where the chart speaks clearly.',
        },
        {
            icon: Users,
            title: 'Human astrologers on standby',
            body: 'Option to take a live consultation from an experienced astrologer — because some questions deserve a human voice.',
        },
        {
            icon: CalendarClock,
            title: 'When, not just whether.',
            body: 'KP gives the timing — your current Maha-Antar-Pratyantar dasha is read against transits to surface fructification windows. Every yes-with-delay reading tells you the dasha period to watch.',
        },
        {
            icon: Compass,
            title: 'Birth-time rectification, built in.',
            body: 'Unsure of your exact minute of birth? The KP Ruling Planets method narrows your birth window down to the minute.',
        },
    ];
    return (
        <section className="relative py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6">
                <div className="max-w-2xl mb-16">
                    <div className="overline mb-4">What makes us different</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        The maths decides. The language stays human.
                    </h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="features-grid">
                    {features.map(({ icon: Icon, title, body }) => (
                        <Card key={title} className="bg-elevated border-marigold/10 rounded-sm p-6 glow-hover">
                            <Icon className="text-marigold mb-5" size={24} />
                            <h3 className="font-serif text-2xl text-parchment mb-3">{title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BirthTimeRectification({ region }) {
    const isIntl = region?.currency === 'USD';
    return (
        <section
            id="rectification"
            className="relative py-24 md:py-32 border-t border-marigold/10"
            data-testid="rectification-section"
        >
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7">
                    <div className="overline mb-4">Birth-time rectification</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment leading-tight">
                        Not sure about your <em className="text-marigold not-italic">birth time?</em>
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">
                        An uncertain birth time means uncertain house cusps — which means
                        less precise readings. AstroConsult uses the{' '}
                        <span className="text-marigold">KP Ruling Planets method</span>{' '}
                        to analyse your birth window and identify your most likely birth
                        time to the minute.
                    </p>

                    <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="rectification-stats">
                        <div className="border-l border-marigold/30 pl-4">
                            <div className="font-mono text-xs text-copper mb-1">Method</div>
                            <div className="font-serif text-xl text-parchment">KP Ruling Planets</div>
                        </div>
                        <div className="border-l border-marigold/30 pl-4">
                            <div className="font-mono text-xs text-copper mb-1">Precision</div>
                            <div className="font-serif text-xl text-parchment">To the minute</div>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <Link to="/signup">
                            <Button
                                className="bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-12 px-7 text-sm tracking-wide"
                                data-testid="rectification-cta-btn"
                            >
                                Rectify my birth time
                                <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                        <span className="text-xs text-slate-500 uppercase tracking-widest" data-testid="rectification-price">
                            One-time service · {isIntl ? '$24' : '₹2,000'}
                        </span>
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div className="relative bg-elevated border border-marigold/15 rounded-sm p-8 glow-hover">
                        <div className="flex items-start gap-4 mb-6">
                            <div className="w-10 h-10 rounded-sm bg-marigold/10 border border-marigold/30 flex items-center justify-center shrink-0">
                                <Compass size={20} className="text-marigold" />
                            </div>
                            <div>
                                <div className="overline mb-1">How it works</div>
                                <h3 className="font-serif text-2xl text-parchment">Three steps to your true Lagna</h3>
                            </div>
                        </div>
                        <ol className="space-y-5">
                            <li className="flex items-start gap-4">
                                <span className="font-mono text-xs text-copper pt-1">01</span>
                                <div>
                                    <div className="text-parchment font-medium">Share your birth window</div>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-1">
                                        Tell us roughly when you were born — a 30 to 60-minute range is enough.
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="font-mono text-xs text-copper pt-1">02</span>
                                <div>
                                    <div className="text-parchment font-medium">Engine scans the sky</div>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-1">
                                        Every sub-sub-lord rising moment in your window is checked against the Ruling Planets
                                        at the time of judgment.
                                    </p>
                                </div>
                            </li>
                            <li className="flex items-start gap-4">
                                <span className="font-mono text-xs text-copper pt-1">03</span>
                                <div>
                                    <div className="text-parchment font-medium">Your birth minute, locked</div>
                                    <p className="text-sm text-slate-400 leading-relaxed mt-1">
                                        The candidate that matches the most ruling planets — and passes the Sub-Sub-Lord
                                        decisive filter — becomes your rectified birth time.
                                    </p>
                                </div>
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    const steps = [
        { n: '01', title: 'Share your birth', body: 'Date, time, place. Geocoded with timezone. Stored once. Used forever.' },
        { n: '02', title: 'Ask a question', body: 'About your life (natal), or a specific yes/no (horary with number 1–249).' },
        { n: '03', title: 'Engine computes', body: 'Significators, house lords, sub-lords, and your current dasha chain — combined into a deterministic verdict.' },
        { n: '04', title: 'Reading delivered', body: 'Easy-to-understand narration with the rationale behind the prediction — the chart, in plain language.' },
    ];
    return (
        <section id="how" className="relative py-24 md:py-32 border-t border-marigold/10">
            <div className="max-w-7xl mx-auto px-6">
                <div className="mb-16">
                    <div className="overline mb-4">How it works</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment max-w-2xl">A consultation, in four quiet steps.</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="how-steps">
                    {steps.map((s) => (
                        <div key={s.n} className="border-l border-marigold/20 pl-6">
                            <div className="font-mono text-xs text-copper mb-3">{s.n}</div>
                            <h3 className="font-serif text-2xl text-parchment mb-2">{s.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Testimonials() {
    const items = [
        {
            quote: "I asked about a job offer in Singapore. The verdict said yes-with-delay — three weeks later the visa came through, exactly as the dasha window predicted. The chart explained why I felt the wait was worth it.",
            author: 'Priya Sharma',
            role: 'Product manager',
            city: 'Mumbai, India',
            initials: 'PS',
        },
        {
            quote: "Skeptical engineer here. The same horary number gave me the same verdict three times. That's not what ChatGPT does — that's a real system. I'm a believer now, at least in the maths.",
            author: 'James Walker',
            role: 'Software engineer',
            city: 'London, UK',
            initials: 'JW',
        },
        {
            quote: "I've consulted KP astrologers in person for fifteen years. AstroConsult's verdict matched my Hyderabad guru's reading down to the sub-lord. Same precision, no five-week wait, no flight booking.",
            author: 'Aisha Khan',
            role: 'Family-business owner',
            city: 'Dubai, UAE',
            initials: 'AK',
        },
        {
            quote: "Asked whether to buy the apartment in Tampines. The reading flagged Saturn–Rahu in my 4th house and said wait six months. I waited. Bought it 20% cheaper after the rate cut. I'll be using AstroConsult for life decisions only from now on.",
            author: 'David Chen',
            role: 'Investor',
            city: 'Singapore',
            initials: 'DC',
        },
        {
            quote: "My grandmother always said the planets listen if you ask kindly. I did not think a website could match her. The reading I got before my mother's surgery was exactly what an old-school jyotishi would have said. Calmed our whole family.",
            author: 'Maya Patel',
            role: 'Pediatrician',
            city: 'Toronto, Canada',
            initials: 'MP',
        },
    ];

    return (
        <section className="relative py-24 md:py-32 border-t border-marigold/10" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-6">
                <div className="mb-16 text-center">
                    <div className="overline mb-4">Trusted worldwide</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">
                        Seekers from <em className="text-marigold not-italic">five continents</em>.
                    </h2>
                    <p className="mt-4 text-slate-400 max-w-xl mx-auto">
                        Mumbai to Manhattan, Dubai to Dunedin — what real users said after their first reading.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-6">
                    {items.map((t, i) => (
                        <Card
                            key={t.author}
                            data-testid={`testimonial-${i}`}
                            className={`bg-elevated border-marigold/15 rounded-sm p-6 glow-hover relative ${
                                i === 0 ? 'lg:col-span-3' : 'lg:col-span-2'
                            }`}
                        >
                            <Quote size={20} className="text-marigold/60 mb-4" />
                            <div className="flex gap-0.5 mb-4">
                                {Array.from({ length: 5 }).map((_, s) => (
                                    <Star key={s} size={12} className="text-marigold fill-marigold" />
                                ))}
                            </div>
                            <p className={`text-slate-200 leading-relaxed font-serif ${i === 0 ? 'text-xl' : 'text-base'}`}>
                                {t.quote}
                            </p>
                            <div className="mt-6 pt-5 border-t border-marigold/10 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-marigold/20 to-copper/30 border border-marigold/30 flex items-center justify-center font-serif text-sm text-parchment">
                                    {t.initials}
                                </div>
                                <div>
                                    <div className="text-parchment font-medium text-sm">{t.author}</div>
                                    <div className="text-xs text-slate-500">{t.role} · {t.city}</div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </section>
    );
}


function Pricing({ region }) {
    const isIntl = region?.currency === 'USD';
    // Round 47 — fetch live pricing (incl. 40% off) from the public
    // endpoint so the landing page mirrors the in-app price + badge.
    const [livePricing, setLivePricing] = useState({});  // {productId: row}
    useEffect(() => {
        const currency = isIntl ? 'USD' : 'INR';
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        fetch(`${apiBase}/api/pricing/public?currency=${currency}`)
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((data) => {
                const map = {};
                for (const row of data?.products || []) {
                    map[row.product] = row;
                }
                setLivePricing(map);
            })
            .catch(() => { /* fallback to hardcoded prices below */ });
    }, [isIntl]);
    const bonusTiers = isIntl ? [
        { range: '$10 – $50',     bonus: '+15%' },
        { range: '$51 – $150',    bonus: '+30%' },
        { range: 'Above $150',    bonus: '+50%' },
    ] : [
        { range: '₹100 – ₹500',    bonus: '+15%' },
        { range: '₹501 – ₹1,000',  bonus: '+30%' },
        { range: 'Above ₹1,000',   bonus: '+50%' },
    ];
    const flatServices = isIntl ? [
        {
            id: 'compatibility',
            name: 'Compatibility reading',
            price: '$25',
            tagline: 'Two charts compared on KP doctrine — primary, positive, star and sub-level gates.',
        },
        {
            id: 'btr',
            name: 'Birth-time analysis',
            price: '$70',
            tagline: 'Pin your exact Lagna using the KP Ruling Planets method. Refunded if inconclusive.',
        },
    ] : [
        {
            id: 'compatibility',
            name: 'Compatibility reading',
            price: '₹500',
            tagline: 'Two charts compared on KP doctrine — primary, positive, star and sub-level gates.',
        },
        {
            id: 'btr',
            name: 'Birth-time analysis',
            price: '₹2,000',
            tagline: 'Pin your exact Lagna using the KP Ruling Planets method. Refunded if inconclusive.',
        },
    ];
    const chatRate = isIntl ? '$1' : '₹25';
    const minTopup = isIntl ? '$3' : '₹100';
    return (
        <section id="pricing" className="relative py-24 md:py-32 border-t border-marigold/10">
            <div className="max-w-7xl mx-auto px-6">
                <div className="mb-16 text-center">
                    <div className="overline mb-4">Pricing</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">Pay only while you chat.</h2>
                    <p className="mt-4 text-slate-400 max-w-xl mx-auto">
                        Top up your wallet, chat with Guruji at {chatRate} per minute, pause whenever you like.
                        Wallet balance never expires.
                        {isIntl && (
                            <span className="block mt-2 text-marigold/80 text-sm" data-testid="pricing-global-note">
                                International payments via PayPal · pay in your local currency
                            </span>
                        )}
                    </p>
                </div>

                {/* Headline card — chat rate */}
                <div className="grid lg:grid-cols-12 gap-6 max-w-5xl mx-auto" data-testid="pricing-grid">
                    <Card
                        data-testid="pricing-chat"
                        className="lg:col-span-7 rounded-sm p-8 relative bg-marigold/5 border-marigold shadow-[0_8px_40px_rgba(245,158,11,0.1)]"
                    >
                        <div className="absolute -top-3 left-8 bg-marigold text-obsidian text-[10px] uppercase tracking-widest px-3 py-1 font-semibold">
                            How it works
                        </div>
                        <div className="overline mb-3">Live chat with Guruji</div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-serif text-5xl text-parchment" data-testid="pricing-chat-rate">{chatRate}</span>
                            <span className="text-slate-400">/ minute</span>
                        </div>
                        <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                            Ask any life question — career, marriage, health, finance.
                            Verdict + dasha timing + three candidate dates, in the same
                            voice an old-school jyotishi would use. Pause when you want.
                        </p>
                        <ul className="mt-6 space-y-3 text-sm text-slate-300">
                            <li className="flex items-start gap-2"><Check size={16} className="text-marigold mt-0.5" /> Natal &amp; Horary readings</li>
                            <li className="flex items-start gap-2"><Check size={16} className="text-marigold mt-0.5" /> Full chart + dasha depth</li>
                            <li className="flex items-start gap-2"><Check size={16} className="text-marigold mt-0.5" /> Multi-language: English, Hindi, Tamil &amp; more</li>
                            <li className="flex items-start gap-2"><Check size={16} className="text-marigold mt-0.5" /> Pause &amp; resume anytime</li>
                        </ul>
                        <Link to="/signup" className="block mt-8">
                            <Button className="w-full rounded-none h-11 bg-marigold text-obsidian hover:bg-marigold-hover">
                                Start chatting
                            </Button>
                        </Link>
                    </Card>

                    {/* Wallet bonus brackets */}
                    <Card
                        data-testid="pricing-bonus"
                        className="lg:col-span-5 rounded-sm p-8 bg-elevated border-marigold/15"
                    >
                        <div className="overline mb-3">Recharge bonus</div>
                        <h3 className="font-serif text-2xl text-parchment">Top up more, get more.</h3>
                        <p className="mt-2 text-sm text-slate-400">
                            Every wallet recharge above {minTopup} earns a loyalty bonus
                            added straight to your balance.
                        </p>
                        <ul className="mt-6 space-y-3" data-testid="pricing-bonus-tiers">
                            {bonusTiers.map((t) => (
                                <li
                                    key={t.range}
                                    data-testid={`pricing-bonus-tier-${t.bonus.replace(/\D/g, '')}`}
                                    className="flex items-center justify-between rounded-sm border border-marigold/15 bg-surface px-4 py-3"
                                >
                                    <span className="text-sm text-slate-300">{t.range}</span>
                                    <span className="font-serif text-2xl text-marigold">{t.bonus}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-5 text-[11px] text-slate-500 uppercase tracking-widest">
                            Wallet never expires · {isIntl ? 'USD via PayPal' : 'INR billing'}
                        </p>
                    </Card>
                </div>

                {/* Flat-rate services */}
                <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-testid="pricing-flat">
                    {flatServices.map((s) => {
                        const live = livePricing[s.id];
                        return (
                            <Card
                                key={s.id}
                                data-testid={`pricing-flat-${s.id}`}
                                className="rounded-sm p-6 bg-elevated border-marigold/15 flex items-start gap-5"
                            >
                                <div className="flex-1">
                                    <div className="overline mb-2">{s.name}</div>
                                    {live && live.has_discount ? (
                                        <div className="flex flex-wrap items-baseline gap-3">
                                            <span className="font-serif text-3xl text-marigold" data-testid={`pricing-flat-${s.id}-final`}>
                                                {formatPrice(live.final_amount, live.currency)}
                                            </span>
                                            <span className="font-serif text-xl text-slate-500 line-through" data-testid={`pricing-flat-${s.id}-list`}>
                                                {formatPrice(live.list_amount, live.currency)}
                                            </span>
                                            <span
                                                className="px-2 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[11px] font-bold uppercase tracking-wider"
                                                data-testid={`pricing-flat-${s.id}-badge`}
                                            >
                                                {live.discount_pct}% OFF
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="font-serif text-3xl text-parchment" data-testid={`pricing-flat-${s.id}-final`}>
                                            {live ? formatPrice(live.final_amount, live.currency) : s.price}
                                        </div>
                                    )}
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.tagline}</p>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Experts() {
    const region = useLandingRegion();
    const isIntl = region?.currency === 'USD';
    // Round 40l — 3 highlighted tiers from the user-supplied 7-tier matrix.
    // Mid-funnel teaser; the actual booking page surfaces all 7 tiers.
    const tiers = isIntl
        ? [
            { label: '15 mins', price: '$42',  scope: 'Quick guidance' },
            { label: '30 mins', price: '$79',  scope: 'Focused session', highlight: true },
            { label: '60 mins', price: '$139', scope: 'Deep dive' },
          ]
        : [
            { label: '15 mins', price: '₹1,249', scope: 'Quick guidance' },
            { label: '30 mins', price: '₹2,449', scope: 'Focused session', highlight: true },
            { label: '60 mins', price: '₹4,699', scope: 'Deep dive' },
          ];
    const fromLabel = isIntl ? 'From $15' : 'From ₹449';
    return (
        <section id="experts" className="relative py-24 md:py-32 border-t border-marigold/10">
            <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                    <div className="overline mb-4">Human consultations</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-parchment">Talk to an astrologer.</h2>
                    <p className="mt-6 text-slate-400 leading-relaxed">
                        When the verdict is big — marriage, job move, surgery — book a 5-minute pulse
                        check or a deep one-hour session with a practiced Vedic or KP consultant.
                        Your chart comes pre-loaded.{' '}
                        <span className="text-marigold/90">{fromLabel}.</span>
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" data-testid="consultation-tiers">
                        {tiers.map((t) => (
                            <div
                                key={t.label}
                                className={`border p-4 rounded-sm ${
                                    t.highlight
                                        ? 'border-marigold/30 bg-marigold/5'
                                        : 'border-marigold/15 bg-elevated'
                                }`}
                            >
                                <div className={`text-xs uppercase tracking-wider ${t.highlight ? 'text-marigold' : 'text-slate-500'}`}>
                                    {t.label}
                                </div>
                                <div className="font-serif text-2xl text-parchment mt-1">{t.price}</div>
                                <div className={`text-[10px] uppercase tracking-wider mt-1 ${t.highlight ? 'text-marigold' : 'text-slate-500'}`}>
                                    {t.scope}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Link to="/signup" className="inline-block mt-8" data-testid="experts-cta">
                        <Button variant="outline" className="bg-transparent border-marigold/40 text-parchment hover:bg-marigold/10 rounded-none h-11 px-6">
                            Browse experts <ArrowRight className="ml-2" size={16} />
                        </Button>
                    </Link>
                </div>
                <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-marigold/15">
                    <img src={FEATURE_IMG} alt="Cosmic abstract" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
                </div>
            </div>
        </section>
    );
}

function Faq() {
    const qs = [
        {
            q: 'How can a deterministic engine decide something spiritual?',
            a: 'We are not deciding truth — we are applying classical Vedic & KP rules consistently. The same question, same inputs, same moment → same verdict. That predictability is the point.',
        },
        {
            q: 'Why does horary use Pune, India?',
            a: "KP Krishnamurti teaches that Prashna charts are cast where the system was codified. We honour the tradition — don't read this as a bug.",
        },
        {
            q: 'Does the LLM ever override the engine?',
            a: 'No. The prose must reflect the engine\u2019s verdict exactly. It is forbidden from flipping yes into no.',
        },
        {
            q: 'Does my wallet balance expire?',
            a: 'Never. Top up once, use it whenever — your wallet balance is yours indefinitely.',
        },
    ];
    return (
        <section id="faq" className="relative py-24 md:py-32 border-t border-marigold/10">
            <div className="max-w-3xl mx-auto px-6">
                <div className="overline mb-4">Questions</div>
                <h2 className="font-serif text-4xl md:text-5xl text-parchment mb-12">Before you ask.</h2>
                <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
                    {qs.map((item, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="border border-marigold/15 bg-surface px-5 data-[state=open]:bg-elevated">
                            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-parchment font-serif text-xl hover:text-marigold py-5">
                                {item.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

function Footer() {
    return (
        <footer className="relative border-t border-marigold/10 py-12">
            <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                    <span className="font-serif text-parchment text-xl">AstroConsult</span>
                    <span className="text-xs text-slate-600">· Vedic KP Jyotish · 2026</span>
                </div>
                <div className="text-xs text-slate-600">
                    For reflection and guidance — not medical or legal advice.
                </div>
            </div>
        </footer>
    );
}
